import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { services } from "@/data/content";
import useSeo from "@/hooks/useSeo";

export default function Services() {
    useSeo({
        title: "Services — 3PL, Warehousing, Transport & Auto Logistics",
        description:
            "Four integrated programs from Beeta: 3PL Logistics, Warehousing, Transporting and Auto-Industry 3PL (IBL, in-plant, reverse). One accountable partner across India.",
        path: "/services",
    });
    return (
        <div data-testid="services-page" className="bg-white">
            {/* HEADER */}
            <section className="relative pt-16 pb-20 bg-slate-50 border-b border-slate-200 overflow-hidden">
                <div className="absolute inset-0 bg-grid-slate opacity-40" />
                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600 mb-5">
                        Services
                    </div>
                    <h1 className="font-display text-4xl sm:text-6xl lg:text-7xl font-black text-slate-900 tracking-tighter leading-[1.08] sm:leading-[0.95] max-w-4xl">
                        Logistics, engineered
                        <br />
                        for operating leaders.
                    </h1>
                    <p className="mt-6 text-lg text-slate-600 max-w-2xl">
                        Four programs. One accountable partner. Every service is built to
                        plug into your existing systems — not replace them.
                    </p>
                </div>
            </section>

            {/* LIST */}
            <section className="py-24 sm:py-32">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-10">
                    {services.map((s, i) => {
                        const Icon = s.icon;
                        return (
                            <motion.div
                                key={s.slug}
                                initial={{ opacity: 0, y: 24 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, amount: 0.2 }}
                                transition={{ duration: 0.6 }}
                                className={`grid lg:grid-cols-12 gap-8 items-stretch rounded-3xl border border-slate-200 overflow-hidden bg-white hover:shadow-xl transition-all ${
                                    i % 2 === 1 ? "lg:[&>*:first-child]:order-2" : ""
                                }`}
                            >
                                <div className="lg:col-span-5 relative aspect-[4/3] lg:aspect-auto">
                                    <img
                                        src={s.image}
                                        alt={s.title}
                                        className="absolute inset-0 w-full h-full object-cover"
                                    />
                                    <div className="absolute top-5 left-5 w-12 h-12 rounded-xl bg-white flex items-center justify-center shadow-lg">
                                        <Icon className="w-6 h-6 text-blue-600" />
                                    </div>
                                </div>
                                <div className="lg:col-span-7 p-8 lg:p-12 flex flex-col justify-center">
                                    <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600 mb-3">
                                        {s.tagline}
                                    </div>
                                    <h2 className="font-display text-3xl sm:text-4xl font-black text-slate-900 tracking-tighter mb-4">
                                        {s.title}
                                    </h2>
                                    <p className="text-slate-600 leading-relaxed text-lg">
                                        {s.description}
                                    </p>
                                    <ul className="mt-6 grid sm:grid-cols-2 gap-3">
                                        {s.highlights.map((h) => (
                                            <li key={h} className="flex items-start gap-2.5 text-sm text-slate-700">
                                                <span className="w-1.5 h-1.5 rounded-full bg-blue-600 mt-2 shrink-0" />
                                                {h}
                                            </li>
                                        ))}
                                    </ul>
                                    <div className="mt-8 flex flex-wrap gap-3">
                                        <Link
                                            to={`/services/${s.slug}`}
                                            data-testid={`service-detail-${s.slug}`}
                                            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-slate-900 text-white text-sm font-semibold hover:bg-blue-600 transition-colors"
                                        >
                                            Read more <ArrowRight className="w-3.5 h-3.5" />
                                        </Link>
                                        <Link
                                            to={`/quote?service=${s.slug}`}
                                            data-testid={`service-quote-${s.slug}`}
                                            className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-slate-300 text-slate-800 text-sm font-semibold hover:bg-slate-50 transition-colors"
                                        >
                                            Get a quote <ArrowUpRight className="w-3.5 h-3.5" />
                                        </Link>
                                    </div>
                                </div>
                            </motion.div>
                        );
                    })}
                </div>
            </section>
        </div>
    );
}
